import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import { getRtkErrorMessage } from 'api/getRtkErrorMessage';
import { getMenuProps } from 'components/ContextMenu';
import { DataTags } from 'components/DataTags';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { usePermission } from 'hooks/usePermission';
import { TColumn, usePersistedColumnsModal } from 'hooks/useSortColumnsModal';
import { useTableSelection } from 'hooks/useTableSelection';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import {
  fetchOrganizationHierarchy,
  organizationMapSelector,
} from 'slices/organizationHierarchy.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { TDocument } from 'types/state.interface';
import { errorNotification, successNotification } from 'utils/notifications';
import {
  TAffiliation,
  TAffiliationFilters,
  TAffiliationOrganizationRole,
} from '@timeedit/types';
import {
  ConfirmationPopup,
  EditableTable,
  GetMoreOptions,
  Icon,
  useInfiniteScroll,
} from '@timeedit/ui-components';
import { Button, Flex, Result, Typography } from '@timeedit/ui-components/antd';
import {
  useBulkDeleteAffiliationsMutation,
  useCountAffiliationsQuery,
  useDeleteAffiliationMutation,
  useLazyFindAffiliationsQuery,
} from '../api/authConfig.api';
import MatchingCriteria from './columns/matchingCriteria';
import { buildAffiliationCopy } from './utils/buildAffiliationCopy';

type Props = {
  authConfigId?: string;
  filter: { searchText?: string; filters: TAffiliationFilters };
  selectedAffiliationId?: string;
};

const ITEM_HEIGHT = 50;
const LIMIT_PER_PAGE = 50;

const AffiliationTable: FC<Props> = ({
  authConfigId,
  filter,
  selectedAffiliationId,
}) => {
  const canEdit = usePermission({
    permissions: ['TE_ADMIN::manageAuthConfigs'],
  });
  const organizationMap = useAppSelector(organizationMapSelector);
  const dispatch = useAppDispatch();

  const [findAffiliations, findAffiliationsState] =
    useLazyFindAffiliationsQuery();
  // Tracks the requestId of the most recent fetch we initiated via getMore.
  // When RTK Query refetches in response to invalidation (e.g. after a save
  // in the drawer), `findAffiliationsState.requestId` changes to a value we
  // didn't initiate — that's our signal to reset the infinite-scroll
  // accumulator below.
  const lastOwnRequestId = useRef<string | undefined>(undefined);
  const [doneInitialFetch, setDoneInitialFetch] = useState(false);
  const [ref, defaultTableProps, height] =
    useDefaultTableProps<TDocument<TAffiliation>>(0);

  useEffect(() => {
    dispatch(fetchOrganizationHierarchy());
  }, []);

  const [error, setError] = useState<FetchBaseQueryError | SerializedError>();

  const getMore = useCallback(
    async (options?: GetMoreOptions) => {
      if (!authConfigId || !options) {
        return {
          data: [],
          page: 1,
          count: 0,
          total: 0,
          hasMore: false,
        };
      }
      const action = findAffiliations(
        {
          ...filter,
          authConfigId: authConfigId,
          page: options.page,
          limit: options.limit,
          sortBy: 'priority:asc&_id:asc',
        },
        true
      );
      lastOwnRequestId.current = action.requestId;
      const result = await action;

      if ('error' in result && result.error != null) {
        setError(result.error);
      }

      if ('data' in result && result.data != null) {
        setError(undefined);
        return {
          data: result.data.results,
          page: result.data.page,
          count: result.data.results.length,
          total: result.data.totalResults,
          hasMore: result.data.page < result.data.totalPages,
        };
      } else {
        return {
          data: [],
          page: 1,
          count: 0,
          total: 0,
          hasMore: false,
        };
      }
    },
    [findAffiliations, authConfigId, filter]
  );

  const {
    data: affiliations = [],
    onScroll,
    loading,
    reset,
    total,
  } = useInfiniteScroll<TDocument<TAffiliation>>({
    getMore,
    itemHeight: ITEM_HEIGHT,
    containerHeight: height,
    limit: LIMIT_PER_PAGE,
    virtual: true,
    initialFetch: doneInitialFetch,
    pagination: false,
  });

  const navigate = useNavigate();
  const [previousFilter, setPreviousFilter] = useState<{
    searchText?: string;
    filters: TAffiliationFilters;
  }>(filter);
  if (filter !== previousFilter) {
    setPreviousFilter(filter);
    reset();
  }

  // Reset on external invalidation. RTK Query refetches the subscribed lazy
  // query whenever a mutation invalidates the AuthAffiliations tag (e.g. after
  // create/update/delete from the drawer). Each refetch produces a new
  // requestId; if we didn't initiate it via getMore (tracked in
  // lastOwnRequestId), the underlying data has changed and the
  // infinite-scroll accumulator needs to rebuild from page 1.
  useEffect(() => {
    if (!findAffiliationsState.requestId) return;
    if (findAffiliationsState.requestId === lastOwnRequestId.current) return;
    reset();
  }, [findAffiliationsState.requestId, reset]);

  if (!doneInitialFetch) {
    setDoneInitialFetch(true);
  }

  const affiliationsWithOrgNames = useMemo(() => {
    const orgName = (id: string) => {
      if (!id) return null;
      return organizationMap[id]?.name ?? id;
    };

    return affiliations.map<AffiliationWithOrgName>((aff) => ({
      ...aff,
      standardOrganizationName: orgName(aff.standardOrganization),
      organizationsAndRoles: aff.organizationsAndRoles.map((o) => ({
        ...o,
        organizationName: orgName(o.organizationId),
      })),
    }));
  }, [organizationMap, affiliations]);

  const {
    idSelectionMode,
    includedIds,
    excludedIds,
    allSelected,
    rowSelection,
    clearSelection,
  } = useTableSelection(affiliationsWithOrgNames);

  const selectedCount = useMemo(() => {
    if (idSelectionMode === 'select') return includedIds.length;
    return total - excludedIds.length;
  }, [excludedIds, idSelectionMode, includedIds, total]);

  const hasSelection = selectedCount > 0;

  const onCreate = useCallback(() => {
    navigate(`?id=new`);
  }, [navigate]);

  const [bulkDeleteAffiliations] = useBulkDeleteAffiliationsMutation();
  const [deleteAffiliation] = useDeleteAffiliationMutation();

  const onDuplicate = useCallback(
    (affiliation: AffiliationWithOrgName) => {
      const rawAffiliation = affiliations.find(
        (a) => a.id === affiliation.id
      ) as TAffiliation;

      navigate('?id=new', {
        state: { affiliation: buildAffiliationCopy(rawAffiliation) },
      });
    },
    [navigate, affiliations]
  );

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const onSingleDelete = useCallback(
    async (affiliation: AffiliationWithOrgName) => {
      confirmDelete(
        affiliation.label,
        async () => {
          if (!authConfigId || !affiliation?.id) return;
          const result = await deleteAffiliation({
            authConfigId,
            affiliationId: affiliation.id,
          });

          if ('error' in result && result.error) {
            errorNotification('Failed to delete affiliation', [
              getRtkErrorMessage(result.error),
            ]);
            return;
          }
          clearSelection();
          successNotification('Affiliation deleted');
        },
        'Delete',
        'Cancel'
      );
    },
    [authConfigId, deleteAffiliation, clearSelection]
  );

  const menuProps = useCallback(
    (affiliation: AffiliationWithOrgName) =>
      getMenuProps({
        record: affiliation,
        onDuplicate: onDuplicate,
        onDelete: onSingleDelete,
      }),
    [onDuplicate, onSingleDelete]
  );

  const onBulkDelete = useCallback(async () => {
    if (!authConfigId || selectedCount === 0) return;

    const result = await bulkDeleteAffiliations({
      authConfigId,
      filters: {
        ...filter.filters,
        ...(idSelectionMode === 'select'
          ? { ids: includedIds }
          : { excludedIds }),
      },
      searchText: filter.searchText,
    });

    if ('error' in result && result.error) {
      errorNotification('Failed to delete affiliations', [
        getRtkErrorMessage(result.error),
      ]);
      return;
    }
    clearSelection();
    successNotification(
      selectedCount === 1
        ? 'Affiliation deleted'
        : `${selectedCount} affiliations deleted`
    );
  }, [
    authConfigId,
    bulkDeleteAffiliations,
    excludedIds,
    filter,
    idSelectionMode,
    includedIds,
    selectedCount,
    clearSelection,
  ]);

  const FooterDisplayCount = useCallback(() => {
    const count = allSelected ? total : selectedCount;
    if (count === 0) return null;
    return <Typography.Text>{`${count} selected`}</Typography.Text>;
  }, [allSelected, selectedCount, total]);

  const footer = useCallback(
    () => (
      <Flex style={{ width: '100%' }} justify="space-between">
        <Flex gap="small" justify="flex-start" align="center">
          <Button
            size="small"
            type="default"
            onClick={onCreate}
            disabled={!canEdit}
          >
            Add affiliation
          </Button>
          {hasSelection ? (
            <ConfirmationPopup
              icon={<Icon alt="Warning" id="Warning" />}
              message={
                selectedCount === 1
                  ? 'Are you sure you want to delete 1 affiliation?'
                  : `Are you sure you want to delete ${selectedCount} affiliations?`
              }
              onConfirm={onBulkDelete}
              okText="Delete"
              cancelText="Cancel"
            >
              <Button size="small" disabled={!canEdit}>
                Delete
              </Button>
            </ConfirmationPopup>
          ) : (
            <Button size="small" disabled>
              Delete
            </Button>
          )}
          <FooterDisplayCount />
        </Flex>
      </Flex>
    ),
    [onCreate, onBulkDelete, hasSelection, FooterDisplayCount, canEdit]
  );

  const { sortColumnsModal, openSortColumnsModal, sortedColumns } =
    usePersistedColumnsModal<Column, AffiliationWithOrgName>(
      columns,
      'affiliations'
    );

  const titleActions = useMemo(
    () => ({
      right: (
        <Button size="small" onClick={openSortColumnsModal}>
          {getText('ui.table.edit_columns')}
        </Button>
      ),
    }),
    [openSortColumnsModal]
  );

  const { data } = useCountAffiliationsQuery(
    authConfigId ? { authConfigId } : skipToken
  );

  const title = useTableTitle<AffiliationWithOrgName>({
    total: data?.total ?? 0,
    filteredAmount: total,
    actions: titleActions,
    resultType: 'affiliations',
  });

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to fetch affiliations"
        subTitle={getRtkErrorMessage(error)}
      />
    );
  }

  return (
    <div ref={ref} style={{ minHeight: height, flexGrow: 1 }}>
      <EditableTable<AffiliationWithOrgName>
        loading={loading}
        dataSource={affiliationsWithOrgNames}
        columns={sortedColumns}
        rowSelection={rowSelection}
        onScroll={onScroll}
        title={title}
        footer={footer}
        getMenuProps={canEdit ? menuProps : undefined}
        onRow={(data) => ({
          onClick: () => navigate(`?id=${data.id}`),
        })}
        rowClassName={(record) =>
          selectedAffiliationId && record.id === selectedAffiliationId
            ? 'affiliation-row--selected'
            : ''
        }
        {...defaultTableProps}
      />
      {sortColumnsModal}
      {contextHolder}
    </div>
  );
};

type AffiliationWithOrgName = TDocument<TAffiliation> & {
  standardOrganizationName: string | null;
  organizationsAndRoles: (TAffiliationOrganizationRole & {
    organizationName: string | null;
  })[];
};

const columns = {
  priority: {
    key: 'priority',
    label: 'Priority',
    resizable: true,
    ellipsis: true,
  },
  label: {
    key: 'label',
    label: 'Description',
    resizable: true,
    ellipsis: true,
  },
  matchingCriteria: {
    key: 'matchingCriteria',
    label: 'Matching Criteria',
    resizable: true,
    ellipsis: true,
    render: (criteria: { attribute: string; value: string }[]) => (
      <MatchingCriteria criteria={criteria} />
    ),
  },
  standardOrganization: {
    key: 'standardOrganization',
    label: 'Standard org.',
    resizable: true,
    ellipsis: true,
    render: (_value: string, record: AffiliationWithOrgName) => (
      <DataTags
        data={
          record.standardOrganizationName
            ? [record.standardOrganizationName]
            : []
        }
      />
    ),
  },
  organizationsAndRoles: {
    key: 'organizationsAndRoles',
    label: 'Org. nodes & roles',
    render: (
      orgNodes: {
        organizationId: string;
        organizationName: string;
        role: string;
      }[]
    ) => (
      <DataTags
        data={orgNodes.map((o) => {
          return `${o.organizationName} - ${o.role}`;
        })}
      />
    ),
    resizable: true,
    ellipsis: true,
  },
  scopes: {
    key: 'scopes',
    label: 'Scopes',
    resizable: true,
    ellipsis: true,
    render: (scopes: string[]) => <DataTags data={scopes} />,
  },
  appPermissions: {
    key: 'appPermissions',
    label: 'App permissions',
    resizable: true,
    ellipsis: true,
    render: (appPermissions: string[]) => <DataTags data={appPermissions} />,
  },
  userObjects: {
    key: 'userObjects',
    label: 'User objects',
    resizable: true,
    ellipsis: true,
    render: (userObjects: string[]) => <DataTags data={userObjects} />,
  },
} as const satisfies Record<string, TColumn<AffiliationWithOrgName>>;

type Column = keyof typeof columns;

export default AffiliationTable;
